import {
  DataCollectionSource,
  FiltersDefinition,
  GroupingDefinition,
  ItemActionsDefinition,
  NavigationFiltersDefinition,
  OneDataCollection,
  RecordType,
  SortingsDefinition,
  SummariesDefinition,
} from '@factorialco/f0-react/dist/experimental'
import { File, Link } from '@factorialco/f0-react/icons/app'
import i18n from 'modules/core/lib/i18n'
import { formatFileSize } from 'modules/trainings/components/Files/utils'
import { DEFAULT_DATA_COLLECTION_STORAGE } from 'modules/trainings/lib/collections'
import { UpsellingKit } from 'modules/upselling_kit/components'
import { AvailableModules } from 'generated/featureDefinitions'
import { ValidReasons } from 'modules/upsell/hooks/useCreateUpsellRequest'
import { DetailOutletContext } from 'modules/trainings/pages/Trainings/Revamp/TrainingDetail/Detail/types'
import { useOutletContext } from 'react-router-dom'
import { F0Box } from '@factorialco/f0-react'

type FileMaterialItem = {
  filename: string
  title?: string | null
  contentType?: string
  size: number
  id: string
  type: 'training' | 'trainingClass' | 'membership'
} & RecordType

type LinkMaterialItem = {
  title: string
  url: string
  type: 'link'
  id: string
}

export type MaterialItem = FileMaterialItem | LinkMaterialItem

export const getSortableTitle = (item: MaterialItem): string => {
  if (item.type === 'link') {
    return item.title
  }
  return item.title || item.filename
}

export const MaterialsList = ({
  source,
  emptyStates,
}: {
  source: DataCollectionSource<
    MaterialItem,
    FiltersDefinition,
    SortingsDefinition,
    SummariesDefinition,
    ItemActionsDefinition<MaterialItem>,
    NavigationFiltersDefinition,
    GroupingDefinition<MaterialItem>
  >
  emptyStates?: {
    'no-data': {
      title: string
      description: string
    }
  }
}) => {
  const { hasContentFF } = useOutletContext<DetailOutletContext>()

  return (
    <>
    {!hasContentFF && (
        <F0Box gap="md" width="1/3" height='s200' alignItems="center" justifyContent="center">
           <UpsellingKit.ProductCard
            moduleRequested={AvailableModules.lms}
            requestSource="Training materials"
            requestReason={ValidReasons.MissingAddon}
            title={i18n.t('trainings.training_catalog.materials.lms_upsell.title')}
            description={i18n.t('trainings.training_catalog.materials.lms_upsell.description')}
          />
        </F0Box>
      )}
    <OneDataCollection
      id="trainings/materials/v1"
      storage={DEFAULT_DATA_COLLECTION_STORAGE}
      emptyStates={emptyStates}
      source={source}
      visualizations={[
        {
          type: 'card',
          options: {
            title: (item: MaterialItem) => {
              switch (item.type) {
                case 'link': {
                  return item.title
                }
                default: {
                  return item.title || item.filename
                }
              }
            },
            description: (item: MaterialItem) => {
              if (item.type === 'link') return ''
              return formatFileSize(item.size || 0)
            },
            cardProperties: [
              {
                label: '',
                icon: Link,
                render: (item: MaterialItem) => {
                  if (item.type !== 'link') return
                  return {
                    type: 'text' as const,
                    value: item.url,
                  }
                },
              },
              {
                label: '',
                icon: File,
                render: (item: MaterialItem) => {
                  // TODO: this should be a check for type file, but
                  // for some god forsaken reason we're getting
                  //  type === 'training' instead of 'file'
                  if (item.type === 'link') return
                  return {
                    type: 'file' as const,
                    value: {
                      name: item.filename,
                      type: item.contentType,
                    },
                  }
                },
              },
            ],
          },
        },
      ]}
    />
    </>
  )
}
